/**
 * News feed with per-symbol LLM scores.
 *
 * Every score is presented as the model's *reading* of the article, and the
 * panel links to the calibration that says whether those readings have
 * preceded price moves. Sentiment without that link is an opinion with a
 * decimal point on it.
 */
import {
  type IngestResponse, type NewsRow, type ScoreResponse, formatPct,
} from "@tt/shared";
import { useState } from "react";

import { useAction, useAsync } from "../hooks/useApi";
import { api } from "../lib/api";

export function NewsPanel({ symbol, watchlist }: { symbol: string; watchlist: string[] }) {
  const [scope, setScope] = useState<"symbol" | "all">("symbol");
  const [days, setDays] = useState(14);
  const [minMateriality, setMinMateriality] = useState(0);

  const feed = useAsync(
    (s) => api.news(scope === "symbol" ? symbol : null, days, 80, s),
    [scope, symbol, days],
  );
  const sentiment = useAsync(
    (s) => api.sentiment(symbol, 30, s),
    [symbol],
    scope === "symbol",
  );
  const ingest = useAction(() =>
    api.ingestNews({ symbols: watchlist, days: Math.min(days, 30), include_filings: true }),
  );
  const score = useAction(() => api.scoreNews({ limit: 50 }));
  const calibrate = useAction(() => api.calibrateNews({ symbols: watchlist }));

  const rows = (feed.data?.news ?? []).filter(
    (n) => (n.materiality ?? 0) >= minMateriality,
  );

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: 10 }}>
        <div className="tf-group">
          <button
            className={`btn small ${scope === "symbol" ? "active" : ""}`}
            onClick={() => setScope("symbol")}
          >
            {symbol}
          </button>
          <button
            className={`btn small ${scope === "all" ? "active" : ""}`}
            onClick={() => setScope("all")}
          >
            All tracked
          </button>
        </div>
        <select className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[2, 7, 14, 30, 90].map((d) => <option key={d} value={d}>{d}d</option>)}
        </select>
        <label className="small">
          min materiality
          <input
            type="range" min={0} max={1} step={0.1}
            value={minMateriality}
            onChange={(e) => setMinMateriality(Number(e.target.value))}
            style={{ width: 86, marginLeft: 6 }}
          />
          <span className="mono"> {minMateriality.toFixed(1)}</span>
        </label>
        <span className="spacer" />
        <button className="btn" disabled={ingest.loading} onClick={() => void ingest.run().then(feed.refetch)}>
          {ingest.loading ? <span className="spinner" /> : null} Fetch news
        </button>
        <button
          className="btn"
          disabled={score.loading || !feed.data?.scoring_available}
          title={feed.data?.scoring_available ? "Score unscored articles with Claude" : "ANTHROPIC_API_KEY is not set"}
          onClick={() => void score.run().then(feed.refetch)}
        >
          {score.loading ? <span className="spinner" /> : null} Score
        </button>
      </div>

      {feed.data?.note && <div className="warnbox">{feed.data.note}</div>}
      {ingest.data && <IngestResult result={ingest.data} />}
      {score.data && <ScoreResult result={score.data} />}
      {(ingest.error || score.error) && (
        <div className="err">{(ingest.error ?? score.error)?.message}</div>
      )}

      {scope === "symbol" && sentiment.data && sentiment.data.articles > 0 && (
        <>
          <h3>Coverage summary · {symbol}</h3>
          <div className="kv">
            <div className="kv-cell">
              <div className="k">weighted tone</div>
              <div
                className="v"
                style={{
                  color: (sentiment.data.sentiment ?? 0) > 0.15 ? "var(--up)"
                    : (sentiment.data.sentiment ?? 0) < -0.15 ? "var(--down)" : undefined,
                }}
              >
                {sentiment.data.sentiment === null ? "—" : sentiment.data.sentiment.toFixed(2)}
              </div>
            </div>
            <div className="kv-cell">
              <div className="k">articles ({sentiment.data.window_days}d)</div>
              <div className="v">{sentiment.data.articles}</div>
            </div>
            <div className="kv-cell">
              <div className="k">peak materiality</div>
              <div className="v">{sentiment.data.max_materiality?.toFixed(2) ?? "—"}</div>
            </div>
          </div>
          <div className="notebox">{sentiment.data.method}</div>
          <div className="row">
            <button className="btn small" disabled={calibrate.loading} onClick={() => void calibrate.run()}>
              {calibrate.loading ? <span className="spinner" /> : null}
              Does this sentiment predict anything?
            </button>
            <span className="faint small">
              Runs the same walk-forward calibration on the model's own scores.
            </span>
          </div>
          {calibrate.data !== null && calibrate.data !== undefined && (
            <NewsCalibration payload={calibrate.data as NewsCalibrationPayload} />
          )}
          {calibrate.error && <div className="err">{calibrate.error.message}</div>}
        </>
      )}

      <h3>
        {scope === "symbol" ? `${symbol} — ` : "All tracked — "}
        {rows.length} article{rows.length === 1 ? "" : "s"}
      </h3>
      {feed.loading && <div className="hint"><span className="spinner" /> loading…</div>}
      {feed.error && <div className="err">{feed.error.message}</div>}
      {!feed.loading && rows.length === 0 && (
        <div className="empty">
          Nothing stored for this window. Press “Fetch news” to ingest.
        </div>
      )}
      <div>{rows.map((n) => <NewsCard key={n.id} row={n} />)}</div>
    </div>
  );
}

function NewsCard({ row }: { row: NewsRow }) {
  const [open, setOpen] = useState(false);
  const s = row.sentiment;
  const tone = s === null ? "flat" : s > 0.15 ? "up" : s < -0.15 ? "down" : "flat";
  const when = new Date(row.published_at);
  return (
    <div
      style={{
        borderBottom: "1px solid var(--border)", padding: "8px 0", cursor: row.rationale ? "pointer" : "default",
      }}
      onClick={() => row.rationale && setOpen((o) => !o)}
    >
      <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
        {row.kind === "filing" && <span className="badge">filing</span>}
        {s !== null && (
          <span className={`badge ${tone === "up" ? "good" : tone === "down" ? "bad" : "muted"}`}>
            {s > 0 ? "+" : ""}{s.toFixed(2)}
          </span>
        )}
        {row.materiality !== null && (
          <span
            className="badge"
            title="Expected size of price impact, independent of direction"
          >
            M {row.materiality.toFixed(2)}
          </span>
        )}
        {row.event_type && <span className="badge muted">{row.event_type}</span>}
        <span className="grow">
          {row.url ? (
            <a
              href={row.url}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: "var(--text)", textDecoration: "none" }}
              onClick={(e) => e.stopPropagation()}
            >
              {row.headline}
            </a>
          ) : (
            row.headline
          )}
        </span>
        <span className="faint small nowrap">
          {row.source} · {when.toLocaleDateString()} {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
      {row.symbols && row.symbols.length > 0 && (
        <div className="faint small mono" style={{ marginTop: 2 }}>
          {row.symbols.slice(0, 8).join(" · ")}
        </div>
      )}
      {open && row.rationale && (
        <div className="notebox" style={{ marginTop: 6 }}>
          <div>{row.rationale}</div>
          <div className="faint small mono" style={{ marginTop: 4 }}>
            novelty {row.novelty?.toFixed(2) ?? "—"} · horizon {row.horizon ?? "—"} · scored by {row.model ?? "—"}
          </div>
        </div>
      )}
    </div>
  );
}

interface NewsCalibrationPayload {
  error?: string;
  hint?: string;
  articles_scored?: number;
  symbols?: number;
  caveat?: string;
  buckets?: {
    signal_id: string;
    label: string;
    horizon_bars: number;
    hit_rate: number;
    baseline_hit_rate: number | null;
    excess_vs_baseline: number;
    n_independent: number;
    q_value: number | null;
    verdict: { verdict: string };
  }[];
}

function NewsCalibration({ payload }: { payload: NewsCalibrationPayload }) {
  if (payload.error) {
    return (
      <div className="warnbox">
        {payload.error}
        {payload.hint ? ` — ${payload.hint}` : ""}
      </div>
    );
  }
  const buckets = payload.buckets ?? [];
  return (
    <>
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>Score bucket</th><th>Horizon</th><th>Hit rate</th>
              <th>Baseline</th><th>Excess</th><th>n</th><th>q</th><th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={`${b.signal_id}-${b.horizon_bars}`}>
                <td>{b.label}</td>
                <td className="num">{b.horizon_bars}d</td>
                <td className="num">{formatPct(b.hit_rate)}</td>
                <td className="num faint">{formatPct(b.baseline_hit_rate)}</td>
                <td className={`num ${b.excess_vs_baseline > 0 ? "up" : "down"}`}>
                  {formatPct(b.excess_vs_baseline)}
                </td>
                <td className="num">{b.n_independent}</td>
                <td className="num">{b.q_value === null ? "—" : b.q_value.toFixed(3)}</td>
                <td className="small">{b.verdict.verdict}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {payload.caveat && <div className="notebox">{payload.caveat}</div>}
    </>
  );
}


/**
 * What a fetch actually did, in a sentence.
 *
 * This was `JSON.stringify(ingest.data)` -- a raw payload with seven keys,
 * shown on success. It reads as a stack trace, which is why a completely
 * successful run got reported as an error.
 */
function IngestResult({ result }: { result: IngestResponse }) {
  const { fetched, written, collapsed, sources, errors } = result;
  const failed = errors && errors.length > 0;
  return (
    <div className={failed ? "warnbox" : "notebox"}>
      <strong>{written}</strong> article{written === 1 ? "" : "s"} stored
      {fetched !== written && <> of {fetched} fetched</>}
      {collapsed > 0 && (
        <>
          {" "}
          — <strong>{collapsed}</strong> duplicate{collapsed === 1 ? "" : "s"} collapsed
        </>
      )}
      {sources?.length > 0 && <> · from {sources.join(", ")}</>}
      {failed && (
        <div className="small" style={{ marginTop: 4 }}>
          {errors.length} source{errors.length === 1 ? "" : "s"} failed: {errors.join("; ")}
        </div>
      )}
    </div>
  );
}

/** What a scoring run cost and how much of it was served from cache. */
function ScoreResult({ result }: { result: ScoreResponse }) {
  const { scored, attempted } = result;
  const u = result.usage;
  const skipped = (attempted ?? 0) - scored;
  return (
    <div className={skipped > 0 ? "warnbox" : "notebox"}>
      <strong>{scored}</strong> article{scored === 1 ? "" : "s"} scored
      {skipped > 0 && <> · {skipped} could not be scored and stay queued</>}
      {u?.cache_hit_ratio !== undefined && (
        // Worth surfacing: near zero means the cached prefix is being
        // invalidated and the run costs several times what it should.
        <> · {Math.round(u.cache_hit_ratio * 100)}% of input served from cache</>
      )}
    </div>
  );
}
